import { useState, useCallback } from 'react';
import type { RenewalFormData } from '../renewalFormTypes';
import { useAcademicRenewalFormTexts } from './useAcademicRenewalFormTexts';

const defaultFormData: RenewalFormData = {
  mainReason: '',
  usageFrequency: [],
  usefulness: 3,
  suggestions: '',
  access: [],
};

export function useAcademicRenewalFormState(initial?: Partial<RenewalFormData>) {
  const [formData, setFormData] = useState<RenewalFormData>({
    ...defaultFormData,
    ...initial,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const t = useAcademicRenewalFormTexts();

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.mainReason) {
      newErrors.mainReason = t.errorMainReason;
    }

    if (!formData.usageFrequency?.length) {
      newErrors.usageFrequency = t.errorUsageFrequency;
    }

    if (!formData.access?.length) {
      newErrors.access = t.errorAccess;
    }

    if (formData.usageFrequency?.includes('other') && !formData.applicationTypeOther?.trim()) {
      newErrors.applicationTypeOther = t.errorApplicationTypeOther;
    }

    if (formData.usefulness < 1 || formData.usefulness > 5) {
      newErrors.usefulness = t.errorUsefulness;
    }

    const hasSuggestions = !!formData.suggestions?.trim();
    if (hasSuggestions && formData.marketingConsent !== 'yes' && formData.marketingConsent !== 'no') {
      newErrors.marketingConsent = t.errorMarketingConsent;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const validateStep = useCallback(
    (step: number): boolean => {
      const newErrors: Record<string, string> = {};

      switch (step) {
        case 0:
          if (!formData.mainReason) newErrors.mainReason = t.errorMainReason;
          break;
        case 1:
          if (!formData.usageFrequency?.length) {
            newErrors.usageFrequency = t.errorUsageFrequency;
          }
          if (formData.usageFrequency?.includes('other') && !formData.applicationTypeOther?.trim()) {
            newErrors.applicationTypeOther = t.errorApplicationTypeOther;
          }
          break;
        case 2:
          if (!formData.access?.length) newErrors.access = t.errorAccess;
          break;
        case 3:
          if (formData.usefulness < 1 || formData.usefulness > 5) {
            newErrors.usefulness = t.errorUsefulness;
          }
          break;
        case 4:
          break;
        case 5:
          if (
            formData.suggestions?.trim() &&
            formData.marketingConsent !== 'yes' &&
            formData.marketingConsent !== 'no'
          ) {
            newErrors.marketingConsent = t.errorMarketingConsent;
          }
          break;
        default:
          break;
      }

      const keysToClear: string[] =
        step === 0
          ? ['mainReason']
          : step === 1
            ? ['usageFrequency', 'applicationTypeOther']
            : step === 2
              ? ['access']
              : step === 3
                ? ['usefulness']
                : step === 5
                  ? ['marketingConsent']
                  : [];

      setErrors(prev => {
        const cleared = { ...prev };
        keysToClear.forEach(k => {
          delete cleared[k];
        });
        return { ...cleared, ...newErrors };
      });

      return Object.keys(newErrors).length === 0;
    },
    [formData, t]
  );

  const handleInputChange = useCallback((field: keyof RenewalFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    setErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
  }, []);

  const setMultiSelect = useCallback((field: 'usageFrequency' | 'access', value: string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      const next = { ...prev };
      if (next[field]) delete next[field];
      if (field === 'usageFrequency' && next.applicationTypeOther) delete next.applicationTypeOther;
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setFormData({ ...defaultFormData });
    setErrors({});
  }, []);

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    t,
    validateForm,
    validateStep,
    handleInputChange,
    setMultiSelect,
    reset,
  };
}
